import { Observable } from "rxjs";
import { yesNoOptions } from "../../constants/components";
import { BaseQuestionDependsOn, QuestionBase, QuestionBaseParams } from "./question-base";
import { FormGroup } from "@angular/forms";

export interface DropdownOption<T = any> {
  value: string | number | boolean;
  label: string;
  disabled?: boolean;
  /** Extra data to pass */
  data?: T;
}

interface DropdownQuestionPayload {
  value?: string;
  parentValue?: string;
  form?: FormGroup;
}

interface DropdownQuestionPaginator {
  page: number;
  pageSize: number;
}

interface DropdownQuestionDependsOn extends BaseQuestionDependsOn {
  callback?: (payload?: DropdownQuestionPayload, paginator?: DropdownQuestionPaginator) => Observable<DropdownOption[]>;
}

interface DropdownQuestionSearchFilter {
  parentKey?: string;
  callback?: (payload?: DropdownQuestionPayload, paginator?: DropdownQuestionPaginator) => Observable<DropdownOption[]>;
  /** Used for local search */
  options?: DropdownOption[];
}

interface DropdownQuestionSearch {
  filter: DropdownQuestionSearchFilter;
  loading: boolean;
  /** Hides the message when there are no results. It has no effect if ``addNewEntry`` is provided  */
  hideNoEntriesFoundLabel?: boolean;
}

interface DropdownQuestionAddNewEntry {
  hide?: boolean;
  action: (form?: FormGroup, dropdown?: DropdownQuestion) => void;
}

export const dropdownOptions = {
  yesNo: [...yesNoOptions],
};

export class DropdownQuestion<T = string | boolean> extends QuestionBase<T> {
  readonly controlType = "dropdown";

  currentOption?: DropdownOption<T>;
  options?: DropdownOption<T>[];
  asyncOptions?: Observable<DropdownOption<T>[]>;
  search?: DropdownQuestionSearch;
  showNullOption?: boolean;
  sortOptions?: boolean;
  addNewEntry?: DropdownQuestionAddNewEntry;
  // Overridden props
  dependsOn?: DropdownQuestionDependsOn;

  constructor(
    params: QuestionBaseParams<T> & {
      currentOption?: DropdownOption;
      options?: DropdownOption[];
      asyncOptions?: Observable<DropdownOption[]>;
      dependsOn?: DropdownQuestionDependsOn;
      search?: Omit<DropdownQuestionSearch, "loading">;
      showNullOption?: boolean;
      sortOptions?: boolean;
      addNewEntry?: DropdownQuestionAddNewEntry;
    },
  ) {
    super({
      key: params.key,
      value: params.value,
      label: params.label,
      required: params.required,
      disabled: params.disabled,
      hide: params.hide,
      order: params.order,
      ignoreInSubmit: params.ignoreInSubmit,
      callbacks: params.callbacks,
      tooltipEnabled: params.tooltipEnabled,
      iconTooltipText: params.iconTooltipText,
    });
    this.currentOption = params.currentOption || null;
    this.options = params.options || [];
    this.asyncOptions = params.asyncOptions ?? null;
    this.dependsOn = params.dependsOn ?? null;
    this.search = { ...params.search, loading: false };
    this.showNullOption = params.showNullOption ?? false;
    this.sortOptions = params.sortOptions ?? true;
    this.addNewEntry = params.addNewEntry ?? null;
  }
}
